'use client';

import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import type { UseFormProps, FieldValues, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export interface ZodFormProps<T extends FieldValues>
  extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit' | 'onError'> {
  schema: z.ZodSchema<T>;
  defaultValues?: UseFormProps<T>['defaultValues'];
  onSubmit: SubmitHandler<T>;
  onError?: (errors: Record<string, unknown>) => void;
  children: React.ReactNode;
  className?: string;
}

export function ZodForm<T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  onError,
  children,
  className = '',
  ...formProps
}: ZodFormProps<T>) {
  const methods = useForm<T>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit, onError)}
        className={className}
        {...formProps}
      >
        {children}
      </form>
    </FormProvider>
  );
}

// Export Zod utilities
export { z } from 'zod';
export { zodResolver } from '@hookform/resolvers/zod';
